import { getRowGroupingFieldFromGroupingCriteria } from "@mui/x-data-grid-premium";
import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import type { ConditionField, SortField } from "../../../../../../api/types/svTypes.ts";
import { AreaItemType, Ordering, ReportType } from "../../../../../../api/types/svTypes.ts";
import {
  getGroupMetaNames,
  getSortType,
  sortSortingFields,
} from "../../components/builder/common/utilities/sortFieldStateHelper";
import { createTabularReportConfiguration } from "../../components/builder/utils/configuration/tabularConfiguration";
import {
  getRowGroupingFieldsInMultipleRowGroupingColumnMode,
  getRowGroupingFieldsInSingleRowGroupingColumnMode,
  separateFrozenColumns,
} from "../../components/viewer/tabular/utils/groupedColumnsHelper.ts";
import {
  ensureDefaultFormatIsSet,
  getAggregationBasedFields,
  isAggregationBased,
} from "../../utilities/fieldsHelper.ts";
import { getDimensionNames } from "../../utilities/getDimensionNames.ts";
import { getMeasureNames } from "../../utilities/getMeasureNames.ts";
import { createTabularDefaultSettings } from "../../utilities/isConfigurationValid.ts";
import { areAllFieldsEmpty, insertItemAt } from "../../utilities/Utilities.tsx";
import { fieldsStateActions } from "../thunks/fieldStatesThunks.ts";
import { tabularFieldsStateThunks } from "../thunks/tabularFieldsStateThunks.ts";
import type { TabularFieldsActionTypes, TabularFieldState } from "../types/fieldsState.types.ts";
import { calculateShowNonZeroBalanceAccounts } from "../utils/calculateShowNonZeroBalanceAccounts.ts";
import {
  calculateAndAssignSystemLabel,
  moveConditionItem,
  moveGroupItem,
  moveItem,
  moveSortingItem,
  removeConditionFromLinked,
  removeConditionItem,
  removeGroupItem,
  removeItem,
  removeSortingItem,
  updateConditionConfig,
  updateConditionItem,
} from "../utils/fieldsState.ts";
import {
  updateLinkAndValidate,
  updateTableField,
  updateTableFieldsOrder,
  validateCustomFields,
} from "../utils/fieldsUtils.ts";
import { TABULAR_SLICE_NAME } from "../utils/storeUtils.ts";
import { isMultipleRowGroupingColumnMode } from "../utils/tabularFieldsSliceHelper.ts";

const initialState: TabularFieldState = {
  conditions: [],
  fields: [],
  grouping: [],
  settings: createTabularDefaultSettings(),
  sorts: [],
  dimensionNames: [],
  measureNames: [],
  configuration: undefined,
};

const tabularFieldsSlice = createSlice({
  name: TABULAR_SLICE_NAME,
  initialState: initialState,
  reducers: {
    resetToDefaultAction: () => initialState,
    // CONDITIONS
    setConditions: (state, action: TabularFieldsActionTypes["setConditions"]) => {
      state.conditions = action.payload;
    },
    addCondition: (state, action: TabularFieldsActionTypes["addCondition"]) => {
      const condition: ConditionField = { ...action.payload.field };
      calculateAndAssignSystemLabel(condition, state.conditions);
      const conditions = insertItemAt(state.conditions, condition, action.payload.index);
      state.conditions = conditions;
    },
    removeCondition: (state, action: TabularFieldsActionTypes["removeCondition"]) => {
      const conditions = removeConditionItem(action.payload, state.conditions);
      removeConditionFromLinked(action.payload.config.guid, getAggregationBasedFields(state.fields));
      state.conditions = conditions;
    },
    moveCondition: (state, action: TabularFieldsActionTypes["moveCondition"]) => {
      state.conditions = moveConditionItem(action.payload.field, action.payload.newIndex, state.conditions);
    },
    updateCondition: (state, action: TabularFieldsActionTypes["updateCondition"]) => {
      state.conditions = updateConditionItem(action.payload.field, action.payload.changes, state.conditions);
    },
    updateConditionConfig: (state, action: TabularFieldsActionTypes["updateConditionConfig"]) => {
      const condition = state.conditions.find((c) => c.config.guid === action.payload.field.config.guid);
      if (condition) {
        condition.config = updateConditionConfig(condition.config, action.payload.changes);
      }
    },
    // FIELDS
    setFields: (state, action: TabularFieldsActionTypes["setFields"]) => {
      state.fields = action.payload;
    },
    addField: (state, action: TabularFieldsActionTypes["addField"]) => {
      state.fields = insertItemAt(state.fields, ensureDefaultFormatIsSet(action.payload.field), action.payload.index);
    },
    removeField: (state, action: TabularFieldsActionTypes["removeField"]) => {
      const item = state.sorts.find((s) => s.config.id === action.payload.config.guid);
      if (item) {
        state.sorts = removeSortingItem(item, state.sorts);
      }
      state.grouping = removeGroupItem({ id: action.payload.config.guid }, state.grouping);

      const fields = removeItem(action.payload, state.fields);
      state.fields = fields;
      if (isAggregationBased(action.payload)) {
        state.conditions = updateLinkAndValidate(
          state.conditions,
          getAggregationBasedFields(fields),
          ReportType.Tabular
        );
      }
    },
    moveField: (state, action: TabularFieldsActionTypes["moveField"]) => {
      state.fields = moveItem(action.payload.field, action.payload.newIndex, state.fields);
    },
    updateFieldConfig: (state, action: TabularFieldsActionTypes["updateFieldConfig"]) => {
      const field = state.fields.find((f) => f.config.guid === action.payload.field.config.guid);
      if (field !== undefined) {
        field.config = { ...field.config, ...action.payload.changes };
        if (isAggregationBased(field)) {
          state.conditions = updateLinkAndValidate(
            state.conditions,
            getAggregationBasedFields(state.fields),
            ReportType.Tabular
          );
        }
      }
    },
    updateMeasureField: (state, action: TabularFieldsActionTypes["updateMeasureField"]) => {
      state.fields = updateTableField(action.payload.field, action.payload.changes, state.fields);
    },
    updateFieldsOrder: (state, action: TabularFieldsActionTypes["updateFieldsOrder"]) => {
      state.fields = updateTableFieldsOrder(state.fields, action.payload);
    },
    updateAllColumnWidths: (state, action: TabularFieldsActionTypes["updateAllColumnWidths"]) => {
      const { width } = action.payload;
      for (const field of state.fields) {
        field.config.customizations = {
          ...field.config.customizations,
          autosize: width === undefined,
          columnWidth: width,
        };
      }
      for (const group of state.grouping) {
        group.customizations = { ...group.customizations, autosize: width === undefined, columnWidth: width };
      }
    },
    updateGroupingColumnAlignment: (state, action: TabularFieldsActionTypes["updateGroupingColumnAlignment"]) => {
      const { field, alignment } = action.payload;
      for (const group of state.grouping) {
        if (
          field === getRowGroupingFieldFromGroupingCriteria(null) ||
          field === getRowGroupingFieldFromGroupingCriteria(group.id)
        ) {
          group.customizations = { ...group.customizations, horizontalAlignment: alignment };
        }
      }
    },
    updateGroupingColumnWidth: (state, action: TabularFieldsActionTypes["updateGroupingColumnWidth"]) => {
      const { field, width } = action.payload;
      for (const group of state.grouping) {
        if (
          field === getRowGroupingFieldFromGroupingCriteria(null) ||
          field === getRowGroupingFieldFromGroupingCriteria(group.id)
        ) {
          group.customizations = { ...group.customizations, autosize: width === undefined, columnWidth: width };
        }
      }
    },
    // GROUPING
    setGrouping: (state, action: TabularFieldsActionTypes["setGrouping"]) => {
      const validatedGroups = action.payload.filter((group) => {
        const field = state.fields.find((f) => f.config.guid === group.id);
        return field !== undefined;
      });
      state.grouping = validatedGroups;
    },
    updateGroup: (state, action: TabularFieldsActionTypes["updateGroup"]) => {
      const group = state.grouping.find((g) => g.id === action.payload.group.id);
      if (group !== undefined) {
        state.grouping[state.grouping.indexOf(group)] = { ...group, ...action.payload.changes };
      }
    },
    addGroup: (state, action: TabularFieldsActionTypes["addGroup"]) => {
      state.grouping = [...state.grouping, action.payload];
    },
    removeGroup: (state, action: TabularFieldsActionTypes["removeGroup"]) => {
      state.grouping = removeGroupItem(action.payload, state.grouping);
    },
    moveGroup: (state, action: TabularFieldsActionTypes["moveGroup"]) => {
      state.grouping = moveGroupItem(action.payload.field, action.payload.newIndex, state.grouping);
      const groupMetaNames = getGroupMetaNames(state.grouping, state.fields);
      const groupedFields = state.sorts.filter((field) => field.config.isGroupField);
      const regularFields = state.sorts.filter((field) => !field.config.isGroupField);
      groupedFields.sort(
        (a, b) =>
          groupMetaNames.findIndex((g) => g === a.meta.name) - groupMetaNames.findIndex((g) => g === b.meta.name)
      );
      state.sorts = [...groupedFields, ...regularFields];
    },
    // SORTING
    setSorting: (state, action: TabularFieldsActionTypes["setSorting"]) => {
      const { fields, groupMetaNames } = action.payload;
      state.sorts = sortSortingFields(fields, groupMetaNames);
    },
    addSorting: (state, action: TabularFieldsActionTypes["addSorting"]) => {
      const { meta, config } = action.payload;
      const sortField = {
        meta: meta,
        config: {
          name: meta.name,
          id: config.guid,
          ordering: Ordering.Ascending,
          caption: config.customLabel || meta.caption,
          isGroupField: false,
          type: getSortType(action.payload),
        },
        areaItemType: AreaItemType.SORTS,
      } satisfies SortField;
      state.sorts = sortSortingFields([...state.sorts, sortField], []);
    },
    addGroupSorting: (state, action: TabularFieldsActionTypes["addGroupSorting"]) => {
      const { field, groupMetaNames } = action.payload;
      state.sorts = sortSortingFields([...state.sorts, field], groupMetaNames);
    },
    removeSorting: (state, action: TabularFieldsActionTypes["removeSorting"]) => {
      state.sorts = removeSortingItem(action.payload, state.sorts);
    },
    moveSorting: (state, action: TabularFieldsActionTypes["moveSorting"]) => {
      state.sorts = moveSortingItem(action.payload.field, action.payload.newIndex, state.sorts);
    },
    updateSortingConfig: (state, action: TabularFieldsActionTypes["updateSortingConfig"]) => {
      const sortField = state.sorts.find((s) => s.meta.name === action.payload.field.meta.name);
      if (sortField) {
        sortField.config = { ...sortField.config, ...action.payload.changes };
      }
    },
    //SETTINGS
    updateSettings: (state, action: TabularFieldsActionTypes["updateSettings"]) => {
      if (
        action.payload.showGroupedColumns !== undefined &&
        action.payload.showGroupedColumns !== (state.settings.showGroupedColumns ?? false)
      ) {
        for (const group of state.grouping) {
          group.customizations = {
            ...group.customizations,
            autosize: true,
            columnWidth: undefined,
            horizontalAlignment: undefined,
          };
        }
      }
      state.settings = { ...state.settings, ...action.payload };
    },
    setSettings: (state, action: TabularFieldsActionTypes["setSettings"]) => {
      state.settings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        fieldsStateActions.setFields.fulfilled,
        fieldsStateActions.updateMeasureField.fulfilled,
        tabularFieldsSlice.actions.setConditions,
        tabularFieldsSlice.actions.addCondition,
        tabularFieldsSlice.actions.removeCondition,
        tabularFieldsSlice.actions.moveCondition,
        tabularFieldsSlice.actions.updateMeasureField
      ),
      (state) => {
        state.conditions = updateLinkAndValidate(
          state.conditions,
          getAggregationBasedFields(state.fields),
          ReportType.Tabular
        );
      }
    );
    builder.addMatcher(
      isAnyOf(
        fieldsStateActions.setFields.fulfilled,
        tabularFieldsStateThunks.updateTabularFieldConfig.fulfilled,
        tabularFieldsSlice.actions.removeField
      ),
      (state) => {
        if (!calculateShowNonZeroBalanceAccounts(state.fields)) {
          state.settings.showNonZeroBalanceAccounts = false;
        }
      }
    );
    builder.addMatcher(
      isAnyOf(
        fieldsStateActions.setFields.fulfilled,
        fieldsStateActions.addField.fulfilled,
        tabularFieldsSlice.actions.removeField,
        tabularFieldsSlice.actions.updateFieldConfig,
        tabularFieldsSlice.actions.updateMeasureField,
        tabularFieldsSlice.actions.setConditions,
        tabularFieldsSlice.actions.addCondition,
        tabularFieldsSlice.actions.moveCondition,
        tabularFieldsSlice.actions.removeCondition,
        tabularFieldsSlice.actions.updateConditionConfig
      ),
      (state) => {
        state.fields = validateCustomFields(state.fields, state.conditions);
      }
    );

    builder.addMatcher(
      isAnyOf(
        tabularFieldsSlice.actions.addGroup,
        tabularFieldsSlice.actions.removeGroup,
        tabularFieldsSlice.actions.moveGroup,
        tabularFieldsSlice.actions.removeField,
        tabularFieldsSlice.actions.moveField
      ),
      (state) => {
        const { groupedFrozenColumns, nonGroupedFrozenColumns } = separateFrozenColumns(
          state.settings.freezedColumns ?? []
        );
        if (groupedFrozenColumns.length === 0 && nonGroupedFrozenColumns.length === 0) {
          return;
        }
        let newGroupedFrozenColumns: string[] = [];
        //There are groups and some columns are pinned
        if (state.grouping.length > 0) {
          if (isMultipleRowGroupingColumnMode(state.settings.showGroupedColumns)) {
            newGroupedFrozenColumns = getRowGroupingFieldsInMultipleRowGroupingColumnMode(state.grouping);
          } else {
            newGroupedFrozenColumns = getRowGroupingFieldsInSingleRowGroupingColumnMode(state.grouping);
          }
        }
        state.settings.freezedColumns = [
          ...newGroupedFrozenColumns,
          ...nonGroupedFrozenColumns.filter(
            (column) =>
              !state.grouping.map((g) => g.id).includes(column) &&
              state.fields.map((f) => f.config.guid).includes(column)
          ),
        ];
      }
    );

    builder.addMatcher(isAnyOf(tabularFieldsSlice.actions.updateSettings), (state, action) => {
      //'Show Grouped Columns' changed
      const isShowGroupedColumnsChanged = action.payload.showGroupedColumns !== undefined;
      if (
        !isShowGroupedColumnsChanged ||
        (state.settings.freezedColumns ?? []).length === 0 ||
        state.grouping.length === 0
      ) {
        return;
      }
      const { groupedFrozenColumns, nonGroupedFrozenColumns } = separateFrozenColumns(
        state.settings.freezedColumns ?? []
      );
      if (groupedFrozenColumns.length === 0) {
        return;
      }

      //Multiple column grouping
      if (isMultipleRowGroupingColumnMode(action.payload.showGroupedColumns)) {
        state.settings.freezedColumns = [
          ...getRowGroupingFieldsInMultipleRowGroupingColumnMode(state.grouping),
          ...nonGroupedFrozenColumns,
        ];
        return;
      }
      //Single column grouping
      state.settings.freezedColumns = [
        ...getRowGroupingFieldsInSingleRowGroupingColumnMode(state.grouping),
        ...nonGroupedFrozenColumns,
      ];
    });
    //This has to be the last matcher
    builder.addMatcher(isAnyOf(...Object.values(tabularFieldsSlice.actions)), (state, action) => {
      if (
        state.configuration &&
        (tabularFieldsSlice.actions.updateSettings.match(action) ||
          tabularFieldsSlice.actions.setSettings.match(action))
      ) {
        if (
          tabularFieldsSlice.actions.updateSettings.match(action) &&
          "grouping" in state.configuration &&
          (state.configuration.settings?.showGroupedColumns ?? false) !== (state.settings.showGroupedColumns ?? false)
        ) {
          state.configuration.grouping = [...state.grouping];
        }
        state.configuration.settings = { ...state.settings };
        return;
      }
      const configuration = createTabularReportConfiguration(
        state.conditions,
        state.fields,
        state.sorts,
        state.grouping,
        state.settings
      );
      state.configuration = configuration;
      const { settings, ...fields } = configuration;
      state.isConfigurationEmpty = areAllFieldsEmpty(fields);
      state.dimensionNames = getDimensionNames(ReportType.Tabular, configuration);
      state.measureNames = getMeasureNames(ReportType.Tabular, configuration);
    });
  },
});

export const tabularFieldsReducer = tabularFieldsSlice.reducer;
